// components/TicketDraggableCard.tsx
import React from "react";
import { useDraggable } from "@dnd-kit/core";
import type { Ticket } from "../types";
import { formatDateTime24, isExpiredEnd } from "../utils/date";

export default function TicketDraggableCard({
    ticket,
    selected,
    onSelect,
    onEdit,
    onDelete,
}: {
    ticket: Ticket;
    selected: boolean;
    onSelect: () => void;
    onEdit?: () => void;
    onDelete?: () => void;
}) {
    const id = `ticket-${ticket.ticketID ?? "new"}`;
    const expired = isExpiredEnd(ticket.ticketTimeEnd);

    const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
        id,
        data: { ticket },
        disabled: !ticket.ticketID || expired,
    });

    const style: React.CSSProperties = {
        position: "relative",
        transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
        opacity: isDragging ? 0.5 : expired ? 0.75 : 1,
        border: selected ? "2px solid #007bff" : "1px solid #ddd",
        borderRadius: 8,
        padding: 10,
        background: "#fff",
        cursor: !ticket.ticketID ? "default" : expired ? "not-allowed" : "grab",
        userSelect: "none",
        filter: expired ? "grayscale(0.9)" : undefined,
    };

    const iconBtn: React.CSSProperties = {
        width: 28,
        height: 28,
        borderRadius: 8,
        border: "1px solid rgba(0,0,0,0.12)",
        background: "#fff",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
        padding: 0,
        lineHeight: 1,
    };

    return (
        <div
            ref={setNodeRef}
            style={style}
            {...attributes}
            {...listeners}
            onClick={onSelect}
            title={
                !ticket.ticketID
                    ? "Gem ticket før du kan trække den"
                    : expired
                        ? "Denne ticket er udløbet og kan ikke tildeles"
                        : "Træk til højre"
            }
        >
            {/* Actions top-right */}
            {(onEdit || onDelete) && (
                <div
                    style={{
                        position: "absolute",
                        top: 8,
                        right: 8,
                        display: "flex",
                        gap: 6,
                        zIndex: 2,
                    }}
                    onPointerDown={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                >
                    {onEdit && (
                        <button
                            type="button"
                            style={iconBtn}
                            title="Edit"
                            onClick={onEdit}
                            disabled={!ticket.ticketID}
                        >
                            ✎
                        </button>
                    )}
                    {onDelete && (
                        <button
                            type="button"
                            style={iconBtn}
                            title="Delete"
                            onClick={onDelete}
                            disabled={!ticket.ticketID}
                        >
                            🗑
                        </button>
                    )}
                </div>
            )}

            {/* Overlay for expired */}
            {expired ? (
                <div
                    style={{
                        position: "absolute",
                        inset: 0,
                        background: "rgba(0,0,0,0.08)",
                        borderRadius: 8,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: 800,
                        letterSpacing: 1,
                        textTransform: "uppercase",
                        pointerEvents: "none",
                    }}
                >
                    UDLØBET
                </div>
            ) : null}

            <div style={{ display: "flex", justifyContent: "space-between", gap: 10, paddingRight: 64 }}>
                <strong style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                    {ticket.ticketDescription}
                </strong>

                <span
                    style={{
                        fontSize: 12,
                        padding: "2px 8px",
                        borderRadius: 999,
                        background: expired ? "#6c757d" : ticket.active ? "#28a745" : "#dc3545",
                        color: "#fff",
                        whiteSpace: "nowrap",
                    }}
                >
                    {expired ? "Udløbet" : ticket.active ? "Active" : "Inactive"}
                </span>
            </div>

            <div style={{ fontSize: 12, marginTop: 6, color: "#444" }}>
                {formatDateTime24(ticket.ticketTimeStart)} → {formatDateTime24(ticket.ticketTimeEnd)}
            </div>

            <div style={{ fontSize: 11, marginTop: 6, color: "#777" }}>
                {ticket.ticketID ? `ID: ${ticket.ticketID}` : "Ny ticket (ikke gemt endnu)"}
            </div>
        </div>
    );
}